// Admin Customer detail. One-fetch profile view: header (name/email/role +
// LTV tiles), then tabs for Orders, Payments, Activity, Library, Notes.
// Works for both registered users (URL param = user id) and guest
// customers (URL param = URL-encoded email).
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'
import { StatusBadge } from './Overview'
import { KpiTile, fmtCents } from '../../components/admin/Charts'

interface OrderRow { id: string; number: number; status: string; totalCents: number; currency: string; createdAt: string; paidAt: string | null; itemCount: number; shippingName: string | null }
interface ActivityRow { id: string; orderId: string; orderNumber: number; kind: string; note: string | null; createdAt: string; byEmail: string | null }
interface PaymentRow { orderId: string; orderNumber: number; status: string; paidAt: string | null; paymentRef: string | null; totalCents: number }
interface UploadRow { id: string; originalName: string; mimeType: string | null; size: number; widthPx: number | null; heightPx: number | null; folderId: string | null; url: string; createdAt: string }
interface FolderRow { id: string; name: string; sortOrder: number; fileCount: number }
interface DesignRow { id: string; kind: string; name: string | null; status: string; priceCents: number | null; proofUrl: string | null; createdAt: string; updatedAt: string }

interface Profile {
  kind: 'registered' | 'guest'
  id: string | null
  email: string
  firstName: string | null; lastName: string | null
  role: string; isActive: boolean
  companyId: string | null
  company: { id: string; name: string } | null
  notes: string | null
  createdAt: string
  lastLoginAt: string | null
}
interface Detail {
  profile: Profile
  stats: { orderCount: number; lifetimeCents: number; totalRevenueCents: number; refundedCents: number; lastOrderAt: string | null }
  orders: OrderRow[]
  activity: ActivityRow[]
  payments: PaymentRow[]
  library: { uploads: UploadRow[]; folders: FolderRow[]; rootCount: number; designs: DesignRow[] }
}

type Tab = 'orders' | 'payments' | 'activity' | 'library' | 'notes'

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>()
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [data, setData] = useState<Detail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('orders')

  async function load() {
    if (!id) return
    try {
      const d = await apiFetch<Detail>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/customers/${id}`)
      setData(d); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [id])

  if (err) return <div className="p-8"><div className="card text-red-700">Error: {err}</div></div>
  if (!data) return <div className="p-8 text-ink-3 text-sm">Loading customer…</div>

  const { profile, stats, orders, activity, payments, library } = data
  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || null

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow"><Link to="/admin/customers" className="hover:text-ink">Customers</Link> · {profile.kind}</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">{fullName ?? profile.email}</h1>
          <div className="text-ink-2 text-sm mt-1 flex items-center gap-3 flex-wrap">
            <span>{profile.email}</span>
            <RoleBadge role={profile.role} />
            {!profile.isActive && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600">disabled</span>}
            {profile.company && (
              <Link to="/admin/companies" className="text-[11px] text-gold-dark hover:underline">at {profile.company.name}</Link>
            )}
          </div>
          <div className="text-[11px] text-ink-3 mt-1">
            Joined {new Date(profile.createdAt).toLocaleDateString()}
            {profile.lastLoginAt && ` · last login ${new Date(profile.lastLoginAt).toLocaleString()}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`mailto:${profile.email}`} className="btn-outline text-xs">Email</a>
          {profile.kind === 'registered' && (
            <Link to="/admin/settings/users" className="btn-outline text-xs">Manage role</Link>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Orders" value={String(stats.orderCount)} sub={stats.lastOrderAt ? `last ${new Date(stats.lastOrderAt).toLocaleDateString()}` : 'no orders yet'} accent="gold" />
        <KpiTile label="Lifetime value" value={fmtCents(stats.lifetimeCents)} sub="all orders incl. cancelled" accent="emerald" />
        <KpiTile label="Net revenue" value={fmtCents(stats.totalRevenueCents)} sub="excludes cancelled / refunded" accent="blue" />
        <KpiTile label="Refunded" value={fmtCents(stats.refundedCents)} sub={stats.refundedCents > 0 ? 'review history' : 'none'} accent={stats.refundedCents > 0 ? 'red' : 'gold'} />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {[
          { key: 'orders',   label: `Orders (${orders.length})` },
          { key: 'payments', label: `Payments (${payments.length})` },
          { key: 'activity', label: `Activity (${activity.length})` },
          { key: 'library',  label: `Media Library (${library.uploads.length + library.designs.length})` },
          { key: 'notes',    label: 'Notes' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key as Tab)}
            className={'px-3 py-2 text-xs font-semibold uppercase tracking-wide ' +
              (tab === t.key ? 'border-b-2 border-gold text-ink' : 'text-ink-3 hover:text-ink')}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'orders' && (
        <div className="card !p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Order</th>
                <th className="text-left px-4 py-2 font-bold">Status</th>
                <th className="text-right px-4 py-2 font-bold">Items</th>
                <th className="text-right px-4 py-2 font-bold">Total</th>
                <th className="text-right px-4 py-2 font-bold">Placed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {orders.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-3 text-sm">No orders yet.</td></tr>}
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-surface transition-colors">
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link to={`/admin/orders/${o.id}`} className="text-gold-dark hover:underline">#{o.number}</Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-right text-ink-2">{o.itemCount}</td>
                  <td className="px-4 py-3 text-right font-semibold text-ink">${(o.totalCents / 100).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-xs text-ink-3">{new Date(o.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'payments' && (
        <div className="card !p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Order</th>
                <th className="text-left px-4 py-2 font-bold">Status</th>
                <th className="text-left px-4 py-2 font-bold">Stripe ref</th>
                <th className="text-right px-4 py-2 font-bold">Amount</th>
                <th className="text-right px-4 py-2 font-bold">Captured</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payments.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-3 text-sm">No payments captured yet.</td></tr>}
              {payments.map((p) => (
                <tr key={p.orderId} className="hover:bg-surface transition-colors">
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link to={`/admin/orders/${p.orderId}`} className="text-gold-dark hover:underline">#{p.orderNumber}</Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 font-mono text-[11px] text-ink-3 truncate max-w-[260px]">{p.paymentRef ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-ink">${(p.totalCents / 100).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-xs text-ink-3">{p.paidAt ? new Date(p.paidAt).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'activity' && (
        <div className="card">
          {activity.length === 0 ? (
            <div className="text-sm text-ink-3 text-center py-8">No production activity yet.</div>
          ) : (
            <ul className="space-y-2">
              {activity.map((a) => (
                <li key={a.id} className="border-l-2 border-gold pl-3">
                  <div className="text-[10px] uppercase tracking-wider text-ink-3 font-bold">{a.kind}</div>
                  <div className="text-sm text-ink-2">{a.note ?? <em className="text-ink-3">(no note)</em>}</div>
                  <div className="text-[11px] text-ink-3">
                    <Link to={`/admin/orders/${a.orderId}`} className="text-gold-dark hover:underline font-mono">#{a.orderNumber}</Link>
                    {' · '}{new Date(a.createdAt).toLocaleString()}
                    {a.byEmail && ` · by ${a.byEmail}`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === 'library' && (
        <LibrarySection profile={profile} library={library} apiSrc={apiSrc} reload={load} getAccessToken={getAccessToken} />
      )}

      {tab === 'notes' && (
        <NotesPanel profile={profile} reload={load} getAccessToken={getAccessToken} />
      )}
    </div>
  )
}

function RoleBadge({ role }: { role: string }) {
  const tone =
    role === 'admin' ? 'bg-gold/15 text-gold-dark' :
    role === 'manager' ? 'bg-blue-100 text-blue-800' :
    role === 'production' ? 'bg-indigo-100 text-indigo-800' :
    role === 'guest' ? 'bg-gray-100 text-gray-600' :
    'bg-emerald-50 text-emerald-700'
  return <span className={'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ' + tone}>{role}</span>
}

function LibrarySection({ profile, library, apiSrc, reload, getAccessToken }: {
  profile: Profile
  library: Detail['library']
  apiSrc: (p: string) => string
  reload: () => Promise<void>
  getAccessToken: unknown
}) {
  const [busy, setBusy] = useState(false)
  if (profile.kind === 'guest') {
    return <div className="card text-sm text-ink-3 text-center py-8">Guest customers don't have a saved library — they checkout without an account.</div>
  }

  async function deleteUpload(u: UploadRow) {
    if (!confirm(`Delete ${u.originalName} from this customer's library forever? This cannot be undone.`)) return
    setBusy(true)
    try {
      await apiFetch(getAccessToken as () => Promise<string | undefined>, `/api/admin/customers/library-uploads/${u.id}`, { method: 'DELETE' })
      await reload()
    } catch (e) { alert((e as Error).message) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      {/* Folders summary */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-2">
          <div className="eyebrow !mb-0">Folders</div>
          <span className="text-[11px] text-ink-3">{library.folders.length} folder{library.folders.length === 1 ? '' : 's'} · {library.rootCount} unfiled</span>
        </div>
        {library.folders.length === 0 ? (
          <div className="text-sm text-ink-3">No folders — everything is in the root view.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {library.folders.map((f) => (
              <span key={f.id} className="text-[11px] px-2 py-0.5 rounded border border-border text-ink-2">
                {f.name} <span className="text-ink-3">({f.fileCount})</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Uploads */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="eyebrow !mb-1">Uploaded artwork</div>
            <div className="text-ink font-semibold">{library.uploads.length} file{library.uploads.length === 1 ? '' : 's'}</div>
          </div>
        </div>
        {library.uploads.length === 0 ? (
          <div className="text-sm text-ink-3 text-center py-8">No uploads.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {library.uploads.map((u) => (
              <div key={u.id} className="relative group border border-border rounded overflow-hidden bg-surface">
                <div className="aspect-square flex items-center justify-center bg-white">
                  <img src={apiSrc(u.url)} alt={u.originalName} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="p-2">
                  <div className="text-[11px] text-ink truncate" title={u.originalName}>{u.originalName}</div>
                  <div className="text-[10px] text-ink-3">
                    {u.widthPx && u.heightPx ? `${u.widthPx}×${u.heightPx}` : '—'}
                    {' · '}{(u.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <button disabled={busy} onClick={() => void deleteUpload(u)}
                  title="Delete forever"
                  className="absolute top-1 right-1 w-5 h-5 rounded bg-white/95 border border-border text-ink-3 hover:text-red-600 hover:border-red-300 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs leading-none shadow-sm disabled:opacity-30">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Saved designs */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="eyebrow !mb-1">Saved designs</div>
            <div className="text-ink font-semibold">{library.designs.length} design{library.designs.length === 1 ? '' : 's'}</div>
          </div>
        </div>
        {library.designs.length === 0 ? (
          <div className="text-sm text-ink-3 text-center py-8">No saved designs.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {library.designs.map((d) => (
              <div key={d.id} className="border border-border rounded p-2 bg-surface">
                <div className="aspect-square bg-white border border-border rounded overflow-hidden flex items-center justify-center mb-2">
                  {d.proofUrl ? <img src={apiSrc(d.proofUrl)} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] uppercase text-ink-3">{d.kind}</span>}
                </div>
                <div className="text-[11px] text-ink truncate">{d.name ?? 'Untitled'}</div>
                <div className="text-[10px] text-ink-3 truncate">{d.kind} · {d.status}</div>
                <div className="text-[10px] text-ink-3">{new Date(d.updatedAt).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function NotesPanel({ profile, reload, getAccessToken }: {
  profile: Profile
  reload: () => Promise<void>
  getAccessToken: unknown
}) {
  const [value, setValue] = useState(profile.notes ?? '')
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  useEffect(() => { setValue(profile.notes ?? '') }, [profile.notes])

  async function save() {
    if (profile.kind === 'guest') return
    setSaving('saving')
    try {
      await apiFetch(getAccessToken as () => Promise<string | undefined>, `/api/admin/customers/${profile.id}/notes`, {
        method: 'PATCH', body: JSON.stringify({ notes: value.trim() || null }),
      })
      setSaving('saved')
      await reload()
      window.setTimeout(() => setSaving('idle'), 1500)
    } catch (e) { setSaving('error'); alert((e as Error).message) }
  }

  if (profile.kind === 'guest') {
    return <div className="card text-sm text-ink-3 text-center py-8">Guest customers can't have notes attached (no user row to save against).</div>
  }

  const dirty = value !== (profile.notes ?? '')

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="eyebrow !mb-1">Internal notes</div>
          <div className="text-ink font-semibold">Private — admin only</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={'text-[11px] ' + (saving === 'saved' ? 'text-emerald-700' : saving === 'error' ? 'text-red-700' : 'text-ink-3')}>
            {saving === 'saving' ? 'Saving…' : saving === 'saved' ? '✓ Saved' : saving === 'error' ? 'Save failed' : ''}
          </span>
          <button onClick={() => void save()} disabled={!dirty || saving === 'saving'} className="btn-gold text-xs px-3 py-1.5">Save</button>
        </div>
      </div>
      <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={8}
        placeholder="Free text — order patterns, contact preferences, anything internal."
        className="input !text-sm" />
    </div>
  )
}
